/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ShiftBatch, ShiftBatchesState, ShiftBatchStatus } from "store/slices/shiftBatches/types";
import { api as shiftsApi } from "store/api/shiftsApi";

import { getShiftBatchStatus } from "../shiftClaimsPolling";

const initialState: ShiftBatchesState = {
    shiftBatches: [],
    selectedDay: new Date(),
};

export const shiftBatchesSlice = createSlice({
    initialState,
    name: "shiftsBatches",
    reducers: {
        setSelectedDay: (state, action: PayloadAction<Date>) => {
            state.selectedDay = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            shiftsApi.endpoints.getShiftBatches.matchFulfilled,
            (state, { payload }) => {
                return { ...state, shiftBatches: payload };
            }
        );
        builder.addMatcher(
            shiftsApi.endpoints.reserveShift.matchFulfilled,
            (state, { payload }) => {
                return {
                    ...state,
                    shiftBatches: state.shiftBatches.map((shift: ShiftBatch) => ({
                        ...shift,
                        status: shift.shiftIds.includes(payload.shiftId)
                            ? ShiftBatchStatus.Pending
                            : shift.status,
                    })),
                };
            }
        );
        builder.addMatcher(
            shiftsApi.endpoints.getShiftClaimsStatuses.matchFulfilled,
            (state, { payload }) => {
                return {
                    ...state,
                    shiftBatches: state.shiftBatches.map((shiftBatch) => {
                        const shiftClaimStatus = payload.find((shiftClaim) =>
                            shiftBatch.shiftIds.includes(shiftClaim.shiftId)
                        );
                        return shiftClaimStatus
                            ? {
                                  ...shiftBatch,
                                  error: shiftClaimStatus.error?.errorKey
                                      ? shiftClaimStatus.error?.errorKey
                                      : null,
                                  status: getShiftBatchStatus(shiftClaimStatus.status),
                              }
                            : shiftBatch;
                    }),
                };
            }
        );
    },
});

const shiftBatchesState = (state: any) => state.shiftBatches;
export const shiftBatchesSelector = createSelector(
    shiftBatchesState,
    ({ shiftBatches }) => shiftBatches
);

export const selectedDaySelector = createSelector(
    shiftBatchesState,
    ({ selectedDay }) => selectedDay
);

export default shiftBatchesSlice.reducer;
